import type { translations as nbTrans } from '../nb'
import { categories } from './categories'
import { countries } from './countries'
import { currencies } from './currencies'
import { emissionItemCategories } from './emissionItemCategories'
import { fuelTypes } from './fuelTypes'
import { ghgCategories } from './ghgCategories'
import { vehicleTypes } from './vehicleTypes'

export const translations: typeof nbTrans = {
	countries,
	currencies,
	fuelTypes,
	vehicleTypes,
	categories,
	emissionItemCategories,
	ghgCategories,
	pagination: {
		previous: 'Previous',
		next: 'Next',
	},
	reports: {
		pageTitle: 'Reports',
		newReport: 'Create a new report',
		newReportNoYears: 'No reporting years found',
		backToReports: 'Back to reports',
		create: 'Create',
		noReports: 'You have not created a report yet',
		noReportsDescription:
			'Here you can create reports on your carbon emissions. They can be easily shared with customers or others, and of course follow the requirements for climate accounting.',
		noReportsNoYears: 'It is not possible to create reports yet',
		noReportsDescriptionNoYears:
			'To create reports, the emissions must first be approved. This is done on the Transactions page.',
		goToTransactions: 'Go to transactions',
		reportInformation: {
			title: 'Report Information',
			baseYear: 'Base year',
			baseYearReasoning: 'Base year reasoning',
			reportingYear: 'Reporting year',
			projects: 'Projects',
			reportTitle: 'Report name',
			continue: 'Continue',
		},
		companyInformation: {
			title: 'Company Information',
			name: 'Name',
			organizationNumber: 'Organization Number',
			organizationNumberShort: 'Org. No.',
			address: 'Address',
			industry: 'Industry',
			lastSubmittedAnnualReport: 'Last Submitted Annual Report',
			registeredInBusinessRegistry: 'Registered in Business Registry',
			registeredInVATRegistry: 'Registered in VAT Registry',
			registeredInEnterpriseRegistry: 'Registered in Enterprise Registry',
			purpose: 'Purpose',
			activity: 'Activity/Industry',
		},
		table: {
			title: 'Title',
			created: 'Created',
			baseYear: 'Base year',
			reportingYear: 'Reporting year',
		},
		actions: {
			view: 'View',
			download: 'Download',
			regenerate: 'Regenerate',
			delete: 'Delete',
		},
	},
	responsiveDisclaimer: {
		title: 'Not optimized for smaller screens',
		description:
			'For the best experience, we recommend using Klara on a larger screen.',
	},
	common: {
		edit: 'Edit',
		save: 'Save',
		reset: 'Reset',
		submit: 'Submit',
		submitting: 'Submitting...',
		cancel: 'Cancel',
		close: 'Close',
		search: 'Search...',
		loading: 'Loading...',
		backToSafety: 'Back to safety',
		actions: 'Actions',
		add: 'Add',
		remove: 'Remove',
		errors: {
			unknown: 'An error occurred. Please try again.',
			userNotFound: 'User not found',
			pageNotFound: "Sorry, the page doesn't exist",
		},

		emissionOriginalValue: 'Original value:',
	},
	navbar: {
		activeImport: 'Importing new transactions',
		navigation: {
			overview: 'Overview',
			accounts: 'Accounts',
			emissions: 'Emissions',
			transactions: 'Transactions',
			dataImport: 'Import data',
			reports: 'Reports',
		},
		menu: {
			trigger: 'Account',
			logout: 'Logout',
			profile: 'Profile',
			settings: 'Settings',
			switchTo: 'Switch to',
			addOrganization: 'Add organization',
			knowledgeBase: 'Knowledge base',
		},
	},
	organizations: {
		pageTitle: 'Organizations',
	},
	emissionFactors: {
		pageTitle: 'Emission Factors',
		newFactor: 'New Emission Factor',
		newFactorDescription:
			'Create a new emission factor. You can also create a lookup configuration to automatically match new emission lines to this factor.',
		name: 'Name',
		factor: 'Factor',
		unit: 'Unit',
		unit_type: 'Unit type',
		region: 'Region',
		year: 'Year',
		sector: 'Sector',
		source: 'Source',
		category: 'Category',
		activityId: 'Activity ID',
		description: 'Description',
		sourceLink: 'Source Link',
		co2eTotal: 'CO2e Total',
		ch4: 'CH4',
		co2: 'CO2',
		n2o: 'N2O',
		co2eOther: 'CO2e Other',
		sourceLcaActivity: 'Source LCA Activity',
		factorCalculationMethod: 'Calculation Method',
		factorCalculationOrigin: 'Calculation Method Source',
		area_unit: 'Area',
		time_unit: 'Time',
		distance_unit: 'Distance',
		data_unit: 'Data',
		energy_unit: 'Energy',
		money_unit: 'Money',
		volume_unit: 'Volume',
		weight_unit: 'Weight',
		unknown: 'Unknown',
		calculationOriginSource: 'Source',
		calculationOriginKlara: 'Klara',
		factorLookupsTitle: 'Factor lookups',
		noLookupsFound: 'No factor lookups found',
		createNewLookup: 'Create new lookup',
		activityIds: 'Activity IDs',
		activityIdsDescription: 'A list of activity IDs, one per line',
		duplicateLookup:
			'An existing lookup configuration with the same values already exists for another emission factor. Click "Replace" to replace the existing lookup configuration.',
		replaceDuplicate: 'Replace',
		lookupCreated: 'Lookup configuration created',
	},
	activities: {
		pageTitle: 'Activities',
		search: 'Search',
		usedBySystem: 'Used internally by Klara',
		create: 'New activity',
		errors: {
			duplicate: 'An activity with the same parameters already exists',
		},
		confirmDeleteDescription: 'Are you sure you want to delete this activity?',
		label: 'Label',
		type: 'Type and variant',
		activityIds: 'Activity IDs',
		description: 'Description',
		accountRanges: 'Account ranges',
		allTypes: 'All',
		unitType: 'Unit type',
		weight: 'Weight',
		volume: 'Volume',
		area: 'Area',
		cost: 'Cost',
		distance: 'Distance',
		time: 'Time',
		energy: 'Energy',
		suggestedActivities: 'Suggested activities',
		foundActivities: `Found {count} {count, plural,
			one {activity}
			other {activities}
		}`,
		noActivitiesFound: 'No activities found for given search term',
		noActivitiesFoundDescription: 'Try a new search term or changing sectors',
	},
	accountMapping: {
		pageTitle: 'Account Mapping',
		accountRange: 'Account range',
		account: 'Account',
		description: 'Description',
		activity: 'Activity',
		scope: 'GHG Scope',
		category: 'GHG Category',
	},
	orgSettings: {
		header: 'Organization settings',
		name: 'Name',
		orgNumber: 'Org Nr.',
		industry: 'Industry',
		sections: {
			integration: 'Integration',
			team: 'Team',
			plan: 'Plan',
			payment: 'Payment',
		},
		integration: {
			title: 'Integration',
		},
	},
	userSettings: {
		header: 'User settings',
		userInfo: {
			name: 'Name:',
			email: 'Email:',
			phone: 'Mobile number:',
		},
		authentication: {
			heading: 'Passkeys',
			noPasskeys: 'You have no passkeys',
			addPasskey: 'Add a passkey',
			noSupport: 'Your browser does not support passkeys',
			passkeysExplanation:
				'Your device supports passkeys, a password replacement that validates your identity using touch, facial recognition, a device password, or a PIN. <br></br> Passkeys can be used for sign-in as a simple and secure alternative to your password and two-factor credentials.',
			signInWithPasskey: 'Sign in with passkey',
			confirmDelete: 'Delete passkey?',
			confirmDeleteDescription: `Are you sure you want to delete your "{description}" passkey? <br></br> By removing this passkey you will no longer be able to use it to sign-in to your account from any of the devices on which it has been synced. <br></br> Note that you may continue to see this passkey as an option during sign-in until you also delete it from your browser, device or associated account's password management settings. `,
			delete: 'Delete',
			passkeyDates: 'Added on {created} | Last used {lastUsed}',
			editPasskeyTitle: 'Edit passkey',
			passkeyDescription: 'Description',
		},
	},
	users: {
		name: 'Name',
		email: 'Email',
		phone: 'Mobile number',
		company: 'Company',
		role: 'Role',
		dateAdded: 'Date added',
		teamHeader: 'Team',
		invitesHeader: 'Invites',
		removeUser: 'Delete team member',
		userOrganizationRoles: {
			OWNER: 'Owner',
			MEMBER: 'Member',
		},
		userOrganizationRoleDescriptions: {
			OWNER: 'Has full access to the organization',
			MEMBER: 'Cannot manage users',
		},
		addOrganizationUser: {
			title: 'Add team member',
			errors: {
				userAlreadyInvited: 'This user has already been invited',
			},

			invitationSent: 'Invitation sent',
			resendInvite: 'Resend invite',
			removeInvite: 'Delete invite',
		},
		removeOrganizationUser: {
			confirm: 'Delete team member?',
			description: 'You are about to delete team member <b>{email}.</b>',
			action: 'Delete',
		},
	},
	invite: {
		heading: 'You have been invited to {organizationName}',
		mustBeCorrectUser:
			'To accept, you must be logged in with email <b>{email}</b>.',
		canCreateUser:
			'Don\'t have a user registered with this email? Click "Create user"',
		alreadyHaveUser: 'Already have a user? Sign in with email <b>{email}</b>',
		createUserCTA: 'Create user',
		loginCTA: 'Sign in',
		acceptInvitation: 'Accept invitation to {organizationName}?',
		decline: 'Decline',
		accept: 'Accept',
	},
	onboard: {
		steps: {
			0: 'Create account',
			1: 'Payment',
			2: 'Import transactions',
		},
		success: {
			title: 'Congratulations – your account is now ready to use!',
			subtitle:
				'You can now go to the import page to monitor progress and adjust import settings.',
			cta: 'Go to the import page',
		},
		product: {
			title: 'How do you want to start?',
			subTitle: 'Choose your plan',
			spend: {
				title: 'Klara Karbon',
				price: 'from 99 NOK/month',
				description:
					'The emissions are calculated through your spendings in the SAF-T file (spend based calculation). Report your emissions in an easy and efficient way.',
			},
			activity: {
				title: 'Klara Karbon AI',
				price: 'from 249 NOK/month',
				description:
					'The emissions are calculated from activities found in your vouchers. Integrates with you accounting system, to help keep your emissons up to date.',
				demoPrompt: 'Try Klara AI without connecting with accounting system?',
				demoAction: 'Start with mock data',
			},
		},
		org: {
			connect: {
				createAccount: 'Create account',
				title: 'Create account for {companyName}',
				explanation:
					'We need to set up an account for your organization. This will allow us to import relevant documents from your accounting system and calculate your greenhouse gas emissions.',
				errors: {
					generic: 'An error occurred when trying to create your account',
					organizationMissingName: 'Organization is missing name',
					organizationAlreadyAdded: 'Organization has already been added',
					noVouchers: 'File contains no vouchers',
				},
				acceptDpa: {
					textStart: 'I have read and agree to the',
					linkText: 'Data Processing Agreement',
					error: 'You have to accept our Data Processing Agreement',
				},
				acceptEula: {
					textStart: 'I accept the',
					linkText: 'End User License Agreement',
					textEnd: '(EULA) governing the use of this software.',
					error: 'You have to accept our End User License Agreement',
				},
				dataSources: {
					tripletex: {
						explanationManual:
							'We need access to your Tripletex account to import relevant documents, allowing us to calculate your greenhouse gas emissions.',
						tripletexToken: 'Tripletex token',
						howToFindToken: {
							textStart: 'How do I find the',
							linkText: 'Tripletex API employee token',
						},
						employeeApiToken: 'Tripletex API employee token',
						updateIntegration: {
							title: 'Update integration',
							description: 'This will update the connection for',
							action: 'Update',
							success: 'Integration updated',
						},
					},
					powerOfficeGo: {
						clientKey: 'PowerOffice Go client key',
						createPowerOfficeGoIntegration: 'Create PowerOffice Go integration',
						explanation:
							'<p>Follow the link below to connect to PowerOffice.</p><p>Note: Add only one client at a time.</p>',
						connect: 'Connect to PowerOffice Go',
						connectAgain: 'Back to PowerOffice Go',
						connectErrors: {
							authorizationUnsuccessful: {
								title: 'The authorization was unsuccessful',
								explanation:
									'Click the button below to return to PowerOffice Go',
							},
							noClient: {
								title: 'No client was selected',
								explanation:
									'Click the button below to return to PowerOffice Go and select a client',
							},
							multipleClients: {
								title: 'Only one client can be added at a time',
								explanation:
									'Click the button below to return to PowerOffice Go and select one client',
							},
							generic: {
								title:
									'An error occurred while creating PowerOffice Go integration',
								explanation:
									'Click the button below to return to PowerOffice Go',
							},
						},
						manualKeyEntry: {
							text: 'Have you already activated the integration in <b>PowerOffice Go</b> and received a client key?',
							linkText: 'Use this form instead',
						},
					},
					twentyFourSevenOffice: {
						explanationManual:
							'We need access to your 24SevenOffice account to import relevant documents, allowing us to calculate your greenhouse gas emissions. <br></br> <a>Follow this guide to find your client ID</a>.',
						clientId: {
							label: '24SevenOffice Client Id',
							placeholder: 'Enter Client Id',
						},
					},
				},
			},
			payment: {
				accountCreated: 'Account is created',
				heading: 'Payment information',
				countryWarning: {
					title: 'Only accouning information from Norway is supported',
					description:
						'The file you have provided is from a different country, and is not currently supported by Klara Karbon.',
				},
				products: {
					basicSubscription: 'Klara Basic',
					analyzedTransactions: 'Import of transactions',
					monthly: 'kr/month',
					perDocument: 'kr per transaction',
					couponValidUntil: 'Offer is valid until {date}',
				},
				form: {
					contactName: 'Contact person',
					contactEmail: 'Contact email',
					companyName: 'Company name',
					organizationNumber: 'Organization number',
					address: 'Address',
					postalCode: 'Postal code',
					postalArea: 'City',
					submit: 'Accept payment terms',
					description:
						"When you accept <a>the payment terms</a>, the business will be invoiced. The invoice will be sent to the contact person's email address.",
				},
			},
			import: {
				paymentAdded: 'Payment information added',
				title: 'Import transactions',
				explanation:
					'We will now review and count your transactions. Please select a period below.',
				from: {
					label: 'From',
					placeholder: 'Select year',
				},
				to: {
					label: 'To',
				},
				importLimit: {
					label: 'Your import limit',
					perMonth: 'per month',
					description:
						'Klara will notify you when an import exceeds your monthly limit. You can change the limit here or under organization settings.',
				},
				startReview: 'Start review',
			},
			importInProgress: {
				title: 'Importing transactions..',
				emailUponCompletion:
					'You will receive an email from us once the import is complete.',
				useWhileImporting:
					'In the meantime, you are welcome to start using Klara. Please note that transactions may still be in the process of being imported.',
				startUsing: 'Start using Klara',
			},
			approval: {
				title: 'Awaiting account approval',
				explanation:
					'During the beta period, all new accounts must be manually approved by us.',
				emailUponApproval:
					'You will receive an email once your account is approved.',
			},
		},
	},
	overview: {
		title: 'Emission overview',
		totalEmissionsTitle: 'Total emissions',
		emissionPerEmployeeTitle: 'Emissions per employee',
		emissionGraphTitle: 'Emissions',
		spendatureGraphTitle: 'Expenditures',
		spendatureGraphUnit: 'NOK',
		scopes: {
			title: 'Emissions by scope',
			descriptions: {
				scope1: 'Direct emissions from sources you control',
				scope2: 'Indirect emissions from electricity, heat, and cooling',
				scope3: 'Other indirect emissions',
			},
			scopeCard: {
				categoryHeader: 'GHG-kategori',
				emissions: 'Utslipp ({unit})',
			},
		},
		pendingTransactionsAlert: {
			title:
				'You have {count} {count, plural, one {transaction} other {transactions}} pending processing',
			description:
				'Go to transactions to view and process the documents, to see updated emission calculation',
			cta: 'View transactions',
		},
	},
	forms: {
		fieldIsRequired: '{field} is required',
		fieldIsInvalid: '{field} is invalid',
		selectField: 'Select {field}',
		retry: 'Try again',
		supportedFormats: 'Only {supportedFormats} files are supported',
		uploadSaft: 'Upload SAF-T',
		uploadError: 'Error processing file: {message}',
		supportedFileTypes: 'Supported formats: {supported}',
	},
	transactions: {
		emptyMessages: {
			pending: {
				title:
					'Great! All transactions matching your selected filters have now been approved.',
				description:
					'You can view the emissions in the Overview or under Emissions. Once Klara imports new vouchers from your accounting system, they’ll automatically appear here again.',
			},
			approved: {
				title: 'No approved transactions yet!',
				description:
					'Head over to "Pending" to review and approve your first transaction',
			},
			discarded: {
				title: 'No discarded transactions',
				description:
					"Discarded transaction won't be part of your emission calculation and report. All the discarded transaction will be collected here",
			},
		},
		goToPending: 'Go to Pending',
		preview: {
			button: 'Preview results',
			noData: 'No data to preview',
			header: 'Preview:',
			topEmitting: 'Top emitting transaction',
		},
		bulkApprove: {
			alertTitle:
				'This will approve {countToApprove} {countToApprove, plural, =0 {documents} one {document} other {documents}}',
			description:
				'All documents with errors will still need manual processing',
			confirm: 'Approve all',
			confirmSelected: 'Approve selected',
			confirmed: 'Approved!',
			onlyErrorsLeft:
				'<p>You cannot approve remaining documents.</p><p>Correct remaining errors before you can approve.</p>',
		},
		bulkSetReviewStatus: {
			alert: {
				title:
					'This will {action} {countToSet} {countToSet, plural, =0 {documents} one {document} other {documents}}',
				actions: {
					approved: 'approve',
					discarded: 'ignore',
					pending: 'reopen',
				},
			},
			description:
				'All documents with errors will still need manual processing',
			confirm: {
				approved: 'Approve selected',
				discarded: 'Ignore selected',
				pending: 'Reopen selected',
			},
			confirmed: {
				approved: 'Approved!',
				discarded: 'Ignored!',
				pending: 'Reopened!',
			},
			onlyErrorsLeft:
				'<p>You cannot approve remaining documents.</p><p>Correct remaining errors before you can approve.</p>',
		},
		listHeader: {
			ledger: 'LEDGER',
			items: 'QUANTITY',
			ledgerNumber: 'LEDGER NUMBER',
			ghg_scope: 'GHG SCOPE',
			status: 'STATUS',
			ledgerDate: 'LEDGER DATE',
			amount: 'AMOUNT',
			emission: 'EMISSION ({unit})',
		},
		fixErroredItem: 'Click here to fix errors to continue calculation',
		filters: {
			title: 'Filters:',
			status: 'Status',
			category: 'Category',
			sender: 'Supplier',
			project: 'Project',
			dates: {
				none: 'All',
				last30d: 'Last 30 days',
				lastMonth: 'Last month',
				lastYear: 'Last year',
				last6m: 'Last six months',
				thisYear: 'This year',
				q1: 'Q1',
				q2: 'Q2',
				q3: 'Q3',
				q4: 'Q4',
			},
			clear: 'Remove filters',
			display: {
				range: 'From {from} to {to}',
				sender: 'Supplier: {sender}',
				project: 'Project: {project}',
				page: 'Page {page}',
			},
		},
		items: {
			titleFallback: 'Unknown sender',
			descriptionFallback: 'Description missing',
		},
		transactionListStatus: {
			approved: 'Approved',
			pending: 'Pending',
			discarded: 'Ignored',
			error: 'Error',
		},
		details: {
			alert: {
				extraction_yields_no_items: {
					title: 'No emission lines found',
					description:
						'You can add emission lines manually or approve/ignore it as it is',
				},
			},
			actions: {
				back: 'Back',
				backToAll: 'Back to all',
				resetToDefault: 'Reset',
				discard: 'Ignore',
				approve: 'Approve',
				returnToPending: 'Reopen',
			},
			header: {
				unknownSender: 'Unknown sender',
				ledgerNumber: 'Ledger number',
				ledgerDate: 'Ledger date',
				status: 'Status',
			},
			edit: {
				sender: {
					header: 'Edit title',
					label: 'Title',
				},
				description: {
					header: 'Edit description',
					label: 'Description',
				},
			},
			items: {
				header: 'Emission lines',
				addEmission: {
					callToAction: 'New emission',
					title: {
						label: 'Emission title',
						placeholder: 'Enter an emission title',
					},
					emissionDate: 'Emission date',
					ghgCategories: {
						label: 'GHG Category',
						placeholder: 'Select GHG category',
					},
					emissionCalculation: 'Emission calculation',
					labels: {
						search: 'Search',
						chooseFromTemplate: 'Choose from template',
						orAddYourOwn: 'Or add your own',
						selectedEmissionFactor:
							'Add your own emission by select emission factor from our library',
						specifiedEmissions: 'Simply add an emission value',
					},
				},
				total: 'Total ({unit})',
				emission: 'Emission',
				removeEmissionItem: {
					confirm: 'Delete emission line?',
					description:
						'You are about to delete the item <b>{itemDescription}.</b><br></br>The amount of emissions released from this line will be removed from the emission calculation.',
					action: 'Delete',
				},
				restoreEmissionItem: {
					confirm: 'Restore emission line?',
					description:
						'You are about to restore the item <b>{itemDescription}.</b><br></br>The amount of emissions released from this line will be added to the emission calculation.',
					action: 'Restore',
				},
				emissionItemCalculation: {
					title: 'Calculation, and information about emission factor',
					calculation: {
						title: 'Calculation',
						formula: 'Formula = Emission factor x quantity',
						emissionFactor: 'Emission factor',
						quantity: 'Quantity',
						convertedFrom: 'converted from',
						total: 'Total',
					},
				},
				emissionEstimationError: 'Error estimating emissions',
				form: {
					revert: 'Revert',
					delete: 'Delete',
					viewCalculation: 'View calculation',
					toOtherSpendingForm: {
						text: 'Only know the amount spent?',
						linkText: 'Switch to spend-based',
					},
					backToStandardForm: 'Switch to activity-based',
					description: {
						placeholder: 'Click to edit title',
					},
					optional: 'optional',
					date: {
						label: 'Date',
					},
					amount: {
						label: 'Amount (excl. VAT)',
						placeholder: 'Enter amount (excl. VAT)',
					},
					currency: {
						label: 'Currency',
						placeholder: 'Select currency',
					},
					category: {
						label: 'Category',
						placeholder: 'Select category',
					},
					country: {
						label: 'Country',
						placeholder: 'Select country',
					},
					fuelType: {
						label: 'Fuel type',
						placeholder: 'Select fuel type',
					},
					units: {
						energy: {
							label: 'Energy',
							placeholder: 'Enter energy',
						},
						liters: {
							label: 'Liters',
							placeholder: 'Enter liters',
						},
						weight: 'Weight',
					},
					dateFrom: {
						label: 'From',
					},
					dateTo: {
						label: 'To',
					},
					travelDate: {
						label: 'Travel date',
					},
					vehicleType: {
						label: 'Type',
						placeholder: 'Select vehicle type',
					},
					distanceCalculation: 'Distance calculation',
					travelFrom: {
						label: 'Travel from',
						placeholder: 'Enter origin',
					},
					travelTo: {
						label: 'Travel to',
						placeholder: 'Enter destination',
					},
					totalDistance: {
						label: 'Total distance',
						placeholder: 'Enter total distance',
					},
					passengers: {
						label: 'Passengers',
						placeholder: 'Enter number of passengers',
					},
					roomNights: {
						label: 'Room nights',
						placeholder: 'Enter number of room nights',
					},
					ghgClassification: {
						label: 'GHG classification',
						placeholder: 'Select GHG classification',
						itemLabel: 'Scope {scope} {category}',
					},
					quantity: {
						label: 'Quantity',
						placeholder: 'Enter quantity',
					},
					roadElectric: {
						label: 'Electric',
						description: 'Select if vehicle is electric',
					},
					specifiedEmission: {
						emissionValue: {
							label: 'Emission value',
							placeholder: 'Enter emission value',
						},
						note: {
							label: 'Note',
							placeholder:
								'Describe the source of this information or how did you calculate it. You can also mention your custom emission factor here.',
						},
						biological: {
							label: 'Biological',
							description:
								'Check if the emission is of biological origin, e.g. biofuels, biogas, and wood',
						},
					},
					guaranteeOfOrigin: {
						label: 'Guarantee of origin',
						description:
							'If there is a guarantee of origin for the electricity, the emissions will be set to 0 in marked based calculations',
					},
					emissionFactor: {
						header: 'Pick emission factor from our library',
						change: 'Change',
						resultsFound: `{results, plural,
											=0 {results}
											one {result}
											other {results}
										} found`,
						apply: 'Apply',
						filter: 'Filter',
						title: 'Emission factors',
						sector: 'Sector',
						placeholderSector: 'Select sector',
						category: 'Category',
						categoryPlaceholder: 'Select category',
						region: 'Region',
						regionPlaceholder: 'Select region',
						unit: 'Unit',
						unitPlaceholder: 'Select unit',
					},
					scope: {
						title: 'Scope',
						placeholder: 'Select scope',
						change: 'Change',
						value: 'Scope {scope}',
					},
				},
				emissionFactor: {
					ef: 'Emission factor',
					aboutEf: 'About emission factor',
					labels: {
						name: 'Name',
						source: 'Source',
						year: 'Year',
						region: 'Region',
						sector: 'Sector',
						category: 'Category',
						unit_type: 'Unit type (S)',
					},
				},
			},
		},
	},
	accounts: {
		listHeader: {
			account: 'Account',
			description: 'Description',
			ghgScope: 'GHG Scope',
			factor: 'Emission Factor',
			amount: 'Amount ({unit})',
			emission: 'Emission ({unit})',
			action: 'Action',
		},
		items: {
			scope: 'Scope {scope}',
		},
		noAccounts: 'No accounts matching provided filters',
		noAccountsDescription:
			'Head to the import page to start an import, or check the status of an ongoing import',
		goToImports: 'Go to the import page',
	},
	scope: 'Scope {scope}',
	draft: 'Draft',
	emissions: {
		filter: {
			scope: {
				all: 'All',
			},
		},
		graph: {
			header: {
				scope: 'Scope {scope}',
				allScopes: 'Scopes 1, 2, and 3',
			},
		},
		tableHeader: {
			emissionItem: 'Emission item',
			ledger: 'Ledger',
			ledgerNumber: 'Ledger No.',
			emissionDate: 'Emission date',
			emission: 'Emission',
		},
		table: {
			noEmissions: 'No emissions found',
		},
	},
	login: {
		title: 'Sign in',
		email: 'Email',
		submit: 'Sign in',
		resend: 'Resend email',
		createAccount: "Don't have an account?",
		cta: 'Create one now',
		emailCheck: 'Check your email',
		emailCheckDescription: `We've sent an email to <b>{email}</b> with a login link. You can now close this page`,
		webauthn: {
			login: 'Login',
			create: 'Register a new credential',
			description: 'description',
			exampleDescription: 'Work laptop',
		},
	},
	register: {
		title: 'Create an account',
		acceptTerms:
			'I consent to the collection and use of my data as described in the',
		privacyPolicy: 'Privacy Policy',
		acceptTermsError: 'You must accept our Privacy Policy',
		haveAccount: 'Already have an account?',
		signIn: 'Sign in',
		submit: 'Create account',
		resend: 'Resend email',
		emailCheck: 'Check your email',
		emailCheckDescription: `We've sent an email to <b>{email}</b> with a link that you can use to set up your organisation. You can now close this page`,
	},
	export: {
		download: 'Export',
		ledgerNumber: 'Ledger number',
		ledgerDate: 'Ledger date',
		sender: 'Sender',
		account: 'Account',
		invoiceNumber: 'Invoice ID',
		description: 'Description',
		emissionDate: 'Emission date',
		ghgScope: 'GHG Scope',
		ghgScopeCategory: 'GHG Category',
		ghgScopeSubcategory: 'GHG Subcategory',
		co2e: 'CO2e (kg)',
		co2: 'CO2 (kg)',
		ch4: 'CH4 (kg)',
		n2o: 'N2O (kg)',
		factor: 'Emission factor',
		factorUnit: 'Unit',
		quantity: 'Quantity',
		activityQuantity: 'Acitivity quantity',
		activityUnit: 'Activity unit',
		factorLink: 'Factor link',
		factorName: 'Factor name',
		factorSource: 'Factor source',
		factorYear: 'Factor year',
		factorRegion: 'Factor region',
		factorCategory: 'Factor category',
		projectId: 'Project ID',
		projectName: 'Project name',
		projectNumber: 'Project number',
		projectStartDate: 'Project start date',
		projectEndDate: 'Project end date',
		projectParentId: 'Project ID parent',
	},
	requestAnalysis: {
		dialogTitle: 'Number of transactions exceeds your limit',
		importedThisMonth: 'Imported this month',
		numberInImport: 'Awaiting import',
		monthlyLimit: 'Monthly limit',
		description:
			'If you click "Accept", your limit will be increased to <b> {limit} </b> + <b> {count} </b> this month. You can change your monthly limit at any time under "Settings".',
		confirm: 'Accept',
	},
	dataImport: {
		heading: 'Data import',
		description:
			'Klara imports only documents relevant for carbon calculation, <b>posted</b> and <b>approved</b> in the accounting system.',
		settings: {
			title: 'Settings',
			limitExcemptionSet:
				'You have set an exception limit of {limit} this month',
			futureImports: 'Import future transactions continuously',
			// Be meg bekrefte, dersom antall transaksjoner i løpet av en måned overstiger
			confirmLimitBreach:
				'Let me confirm if the number of transactions in a month exceeds',
		},
		// Bilag forberedt/importert av Klara så langt
		totalVouchers: 'Transactions imported by Klara so far',
		ongoingImport: {
			headingImporting: 'Preparing transactions',
			headingAnalyzing: 'Importing transactions',
			status: '{analyzed} of {total}',
			mightTakeSomeTime:
				'This might take some time. You will receive an email when the import is complete.',
		},
		import: 'Import transactions',
		startImport: 'Start import',
		goToTransactions: 'Go to transactions',
		goToOverview: 'Go to overview',
		fileWarnings: {
			differentOrgNumber: 'File contains different organisation number',
			differentCountry: 'File contains different country',
			noVouchers: 'File contains no vouchers',
			noValidDateRange: 'File does not contain a valid date range',
			overlappingImports: 'File contains data that has already been imported',
		},
	},
}
